import { COLORS } from '../style.js';

/**
 * v0.9.9 Hourglass — shared formatter for the 24-hour turn timer.
 *
 * Granularity (locked at design time):
 *   - > 1 hour:    "23H LEFT"           (per-minute refresh ok)
 *   - 1m–1h:       "47M LEFT"           (per-minute refresh ok)
 *   - 10s–60s:     "47S LEFT"           (per-second refresh, red tint)
 *   - 0 / past:    "EXPIRED"            (red, will skip on next cron)
 *
 * "OPPONENT" prefix added on the opponent's turn so the muted chip
 * reads "OPPONENT 18H" / "OPPONENT 47M" — different label but same
 * countdown math. The action panel callsite shows this in slot 3
 * (replaces the live-SP MM:SS chip); the inbox card subtitle uses
 * the same labels.
 */
export interface TurnTimerDisplay {
  readonly label: string;
  readonly color: string;
  readonly strokeColor: number;
}

export function formatTurnTimer(
  remainingMs: number,
  isLocalTurn: boolean
): TurnTimerDisplay {
  if (remainingMs <= 0) {
    return {
      label: 'EXPIRED',
      color: COLORS.accentRedHex,
      strokeColor: COLORS.accentRedInt,
    };
  }

  const remainingSec = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(remainingSec / 3600);
  const minutes = Math.floor((remainingSec % 3600) / 60);
  const seconds = remainingSec % 60;

  // Color: bright cyan on your turn, muted gray on opponent's. Red
  // tint when under a minute (countdown urgency, same on either side).
  const underMinute = hours === 0 && minutes === 0;
  const color = underMinute
    ? COLORS.accentRedHex
    : isLocalTurn
      ? COLORS.accentCyanHex
      : COLORS.textMuted;
  const strokeColor = underMinute
    ? COLORS.accentRedInt
    : isLocalTurn
      ? COLORS.accentCyanInt
      : COLORS.borderSubtleInt;

  const prefix = isLocalTurn ? '' : 'OPPONENT ';
  let core: string;
  if (hours >= 1) {
    core = `${hours}H LEFT`;
    if (!isLocalTurn) core = `${hours}H`;
  } else if (minutes >= 1) {
    core = `${minutes}M LEFT`;
    if (!isLocalTurn) core = `${minutes}M`;
  } else {
    core = `${seconds}S LEFT`;
    if (!isLocalTurn) core = `${seconds}S`;
  }

  return { label: `${prefix}${core}`, color, strokeColor };
}

/**
 * Compact form for the inbox card subtitle — drops "OPPONENT" prefix
 * (the inbox card already has its own state badge) and uses
 * "in <unit>" prose. v0.9.9 Hourglass.
 *
 *   "Turn expires in 23h"
 *   "Turn expires in 47m"
 *   "Turn expires in 47s"
 *   "Turn expired"
 */
export function formatInboxTurnExpiry(remainingMs: number): string {
  if (remainingMs <= 0) return 'Turn expired';
  const remainingSec = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(remainingSec / 3600);
  const minutes = Math.floor((remainingSec % 3600) / 60);
  const seconds = remainingSec % 60;
  if (hours >= 1) return `Turn expires in ${hours}h`;
  if (minutes >= 1) return `Turn expires in ${minutes}m`;
  return `Turn expires in ${seconds}s`;
}
